import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDetailsDrawer } from './AppDetailsDrawerContext';
import {
  Box, Paper, Typography, CircularProgress, Grid, Alert, Stack, Chip, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Avatar, Menu, MenuItem, ListItemIcon, ListItemText,
  Checkbox, List, ListItem,
} from '@mui/material';
import { PieChart } from './PieChartComponent';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import {
  Apple, Android, Close as CloseIcon, OpenInNew, Download, Image as ImageIcon,
  TrendingUp, TrendingDown, TrendingFlat, ViewModule, RocketLaunchOutlined, Whatshot,
  ContentCopy,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { useAuth } from './AuthContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface AppDistributionDashboardProps { isActive: boolean }

type CardId = 'summary' | 'androidPie' | 'iosPie' | 'platformBar' | 'statusBar' | 'topApps';
const ALL_CARDS: Array<{ id: CardId; label: string }> = [
  { id: 'summary', label: 'Summary tiles' },
  { id: 'androidPie', label: 'Android version pie' },
  { id: 'iosPie', label: 'iOS version pie' },
  { id: 'platformBar', label: 'Platform distribution' },
  { id: 'statusBar', label: 'Deployment status' },
  { id: 'topApps', label: 'Top 10 most-built apps' },
];
const CARDS_VISIBILITY_KEY = 'distribution:cards:v1';

type DrillFilter = { type: 'platform' | 'status' | 'androidVersion' | 'iosVersion'; value: string; label: string } | null;

const platformIcon = (p: string) => {
  const pl = (p || '').toLowerCase();
  if (pl === 'ios') return <Apple sx={{ fontSize: 14 }} />;
  if (pl === 'android') return <Android sx={{ fontSize: 14 }} />;
  return null;
};

const platformColor = (p: string) => {
  const pl = (p || '').toLowerCase();
  if (pl === 'ios') return '#0f172a';
  if (pl === 'android') return '#3DDC84';
  return '#94a3b8';
};

const statusColor = (s: string) => {
  const ss = (s || '').toLowerCase();
  if (/success/.test(ss)) return '#16a34a';
  if (/fail/.test(ss)) return '#dc2626';
  if (/cancel/.test(ss)) return '#f59e0b';
  if (/queue|running|pending/.test(ss)) return '#3b82f6';
  return '#64748b';
};

// ----- CSV escaping (formula-injection safe) -----
const csvEscape = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ----- Delta arrow helper -----
const DeltaArrow: React.FC<{ current: number; previous?: number | null }> = ({ current, previous }) => {
  if (previous === undefined || previous === null) {
    return <Typography variant="caption" color="text.secondary">no week-ago data</Typography>;
  }
  const delta = current - previous;
  if (delta === 0) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#64748b' }}>
        <TrendingFlat sx={{ fontSize: 16 }} />
        <Typography variant="caption">no change vs last week</Typography>
      </Stack>
    );
  }
  const up = delta > 0;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: up ? '#16a34a' : '#dc2626' }}>
      {up ? <TrendingUp sx={{ fontSize: 16 }} /> : <TrendingDown sx={{ fontSize: 16 }} />}
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {up ? '+' : ''}{delta} vs last week
      </Typography>
    </Stack>
  );
};

export const AppDistributionDashboard: React.FC<AppDistributionDashboardProps> = ({ isActive }) => {
  const { openAppDetails: openAppDetailsDrawer } = useAppDetailsDrawer();
  const [searchParams, setSearchParams] = useSearchParams();
  const { appData, fetchDashboardData } = useAuth();

  // Trigger the dashboard fetch when this page is opened standalone (no
  // previous Home-page visit). Without this, `appData` stays empty and the
  // drill-down modal reports "0 apps matching" even though the charts show
  // real counts (those come from a separate analytics endpoint).
  useEffect(() => {
    if (isActive && (!appData || Object.keys(appData).length === 0)) {
      fetchDashboardData();
    }
  }, [isActive, appData, fetchDashboardData]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [latestTag, setLatestTag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [topApps, setTopApps] = useState<any[]>([]);
  const [topAppsLoading, setTopAppsLoading] = useState(false);

  // Drill-down modal driven by URL.
  const drillFromUrl = useMemo<DrillFilter>(() => {
    const raw = searchParams.get('modal');
    if (!raw) return null;
    const [type, value] = raw.split(':');
    if (!type || !value) return null;
    if (!['platform', 'status', 'androidVersion', 'iosVersion'].includes(type)) return null;
    return { type: type as any, value, label: `${type === 'androidVersion' ? 'Android version' : type === 'iosVersion' ? 'iOS version' : type}: ${value}` };
  }, [searchParams]);
  const [drill, setDrill] = useState<DrillFilter>(drillFromUrl);
  useEffect(() => { setDrill(drillFromUrl); }, [drillFromUrl]);

  const openDrill = useCallback((f: NonNullable<DrillFilter>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('modal', `${f.type}:${f.value}`);
      return next;
    });
  }, [setSearchParams]);
  const closeDrill = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('modal');
      return next;
    });
  }, [setSearchParams]);

  // ----- Customizable cards visibility -----
  const [visibleCards, setVisibleCards] = useState<Set<CardId>>(() => {
    try {
      const raw = localStorage.getItem(CARDS_VISIBILITY_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as CardId[];
        return new Set(arr);
      }
    } catch { /* corrupted — use default */ }
    return new Set(ALL_CARDS.map(c => c.id));
  });
  const [cardsMenuAnchor, setCardsMenuAnchor] = useState<null | HTMLElement>(null);
  const toggleCard = (id: CardId) => {
    setVisibleCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(CARDS_VISIBILITY_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  // ----- Refs for PNG export -----
  const pageRef = useRef<HTMLDivElement>(null); // whole-page capture for html2canvas
  const platformChartRef = useRef<any>(null);
  const statusChartRef = useRef<any>(null);

  const fetchData = useCallback(async () => {
    if (!isActive) return;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const analyticsRes = await axios.get('/api/analytics/data', { headers, timeout: 30000 });
      setData(analyticsRes.data);
      setError(null);
    } catch (err) {
      console.error('Analytics Fetch Error:', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }

    axios.get('/api/appdata/gitlab-tags', { headers, timeout: 8000 })
      .then(res => setLatestTag(res.data?.tags?.[0]?.name || null))
      .catch(err => console.warn('GitLab tags fetch failed (non-blocking):', err?.message));

    // Top apps — separate fetch so a slow worker doesn't block the main
    // charts. Tries the cached digest first; falls back to the legacy
    // `/top-apps` endpoint so a partial deploy (frontend ahead of worker)
    // still shows the table instead of an empty card.
    setTopAppsLoading(true);
    const fetchTopApps = async (): Promise<any[]> => {
      try {
        const res = await axios.get('/api/builds/top-apps/digest?days=30&limit=10', { headers, timeout: 20000 });
        return res.data?.items || [];
      } catch (digestErr: any) {
        if (axios.isCancel?.(digestErr) || digestErr?.name === 'CanceledError') throw digestErr;
        console.warn('[AppDistribution] /top-apps/digest unavailable, falling back to /top-apps:', digestErr?.message);
        const res = await axios.get('/api/builds/top-apps?days=30&limit=10', { headers, timeout: 20000 });
        return res.data?.items || [];
      }
    };
    fetchTopApps()
      .then(items => setTopApps(items))
      .catch(err => console.warn('Top apps fetch failed (non-blocking):', err?.message))
      .finally(() => setTopAppsLoading(false));
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      fetchData();
      const interval = setInterval(fetchData, 180000);
      return () => clearInterval(interval);
    }
  }, [isActive, fetchData]);

  // ----- Derived: all apps as array (from auth context) -----
  const allApps = useMemo(() => {
    if (!appData) return [];
    return Object.values(appData) as any[];
  }, [appData]);

  // Version distributions computed from the SAME feed the drill filters, so a
  // version chip can never show a bucket the drill then reports as "0 apps".
  // (The analytics endpoint is a separate source and drifted from the feed.)
  const versionCounts = useMemo(() => {
    const android: Record<string, number> = {};
    const ios: Record<string, number> = {};
    allApps.forEach((a: any) => {
      const p = (a.platform || '').toLowerCase();
      const v = p === 'android' ? a.android_version : p === 'ios' ? a.ios_version : null;
      if (!v || v === 'N/A') return;
      const bucket = p === 'android' ? android : ios;
      bucket[v] = (bucket[v] || 0) + 1;
    });
    // Highest version first.
    const sortDesc = (o: Record<string, number>) =>
      Object.fromEntries(Object.entries(o).sort((a, b) => b[0].localeCompare(a[0], undefined, { numeric: true })));
    return { android: sortDesc(android), ios: sortDesc(ios) };
  }, [allApps]);


  // ----- Drill-down filter -----
  const drillResults = useMemo(() => {
    if (!drill) return [];
    return allApps.filter(app => {
      const p = (app.platform || '').toLowerCase();
      const s = (app.status || '').toLowerCase();
      const av = app.android_version || '';
      const iv = app.ios_version || '';
      if (drill.type === 'platform') {
        if (drill.value === 'other') return p !== 'ios' && p !== 'android';
        return p === drill.value;
      }
      if (drill.type === 'status') return s === drill.value.toLowerCase();
      if (drill.type === 'androidVersion') return p === 'android' && av === drill.value;
      if (drill.type === 'iosVersion') return p === 'ios' && iv === drill.value;
      return false;
    });
  }, [allApps, drill]);

  const openAppDetails = (host: string, platform?: string, appId?: string | number) => {
    closeDrill();
    // Open the details slider over the current page. Pass stable identifiers so
    // dual-platform / duplicate-host apps resolve to the correct entry.
    openAppDetailsDrawer(host, platform, appId);
  };

  // ----- Exports -----
  const exportCsv = () => {
    if (!data) return;
    const rows: string[] = [];
    rows.push('Section,Key,Value');
    rows.push(['Total', 'totalApps', data.totalApps ?? 0].map(csvEscape).join(','));
    Object.entries(data.platformCounts || {}).forEach(([k, v]) => {
      rows.push(['Platform', k, v as number].map(csvEscape).join(','));
    });
    Object.entries(data.statusCounts || {}).forEach(([k, v]) => {
      rows.push(['Status', k, v as number].map(csvEscape).join(','));
    });
    Object.entries(data.androidVersions || {}).forEach(([k, v]) => {
      rows.push(['Android version', k, v as number].map(csvEscape).join(','));
    });
    Object.entries(data.iosVersions || {}).forEach(([k, v]) => {
      rows.push(['iOS version', k, v as number].map(csvEscape).join(','));
    });
    topApps.forEach(t => {
      rows.push(['Top-built', t.composerHost, t.total].map(csvEscape).join(','));
    });
    const csv = rows.join('\r\n');
    downloadFile(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `app-distribution-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // PNG export — captures the entire dashboard (all cards + charts) to a single
  // image via html2canvas, not just one chart.
  const exportPng = async () => {
    const node = pageRef.current;
    if (!node) return;
    try {
      const canvas = await html2canvas(node, {
        backgroundColor: '#ffffff',
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: true,
        // Skip the toolbar (search/menu/export buttons) so the export is just
        // the report content.
        ignoreElements: (el: Element) => el.getAttribute?.('data-export-ignore') === 'true',
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `app-distribution-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('PNG export failed', e);
    }
  };

  // ----- Chart data + click handlers -----
  const platformData = {
    labels: ['Android', 'iOS', 'Other'],
    datasets: [{
      label: 'Apps by Platform',
      data: [
        data?.platformCounts?.android || 0,
        data?.platformCounts?.ios || 0,
        data?.platformCounts?.other || 0,
      ],
      backgroundColor: ['#3ddc84', '#007AFF', '#888888'],
    }],
  };

  const statusLabels = Object.keys(data?.statusCounts || {});
  const statusValues = Object.values(data?.statusCounts || {});
  const statusData = {
    labels: statusLabels,
    datasets: [{
      label: 'Apps by Status',
      data: statusValues,
      backgroundColor: 'rgba(53, 162, 235, 0.5)',
    }],
  };

  // Week-over-week deltas — pulled from analytics payload (set by backend
  // snapshot logic). May be undefined the first week after this ships, in
  // which case DeltaArrow renders a neutral placeholder.
  const weekAgo = data?.weekAgo as undefined | { platformCounts?: Record<string, number>; statusCounts?: Record<string, number>; totalApps?: number };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box ref={pageRef} sx={{ p: 3, width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a202c' }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Data cached and refreshed every 3 minutes · Last updated {new Date(data?.timestamp || Date.now()).toLocaleTimeString()}
          </Typography>
        </Box>

        <Stack data-export-ignore="true" direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
          <Tooltip title="Show / hide cards" arrow>
            <IconButton onClick={e => setCardsMenuAnchor(e.currentTarget)} sx={{ border: '1px solid #e2e8f0', borderRadius: 1, width: 40, height: 40 }}>
              <ViewModule fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={cardsMenuAnchor}
            open={Boolean(cardsMenuAnchor)}
            onClose={() => setCardsMenuAnchor(null)}
            PaperProps={{ sx: { mt: 0.5, minWidth: 260 } }}
          >
            {ALL_CARDS.map(card => (
              <MenuItem key={card.id} onClick={() => toggleCard(card.id)} dense>
                <ListItemIcon><Checkbox edge="start" checked={visibleCards.has(card.id)} tabIndex={-1} disableRipple size="small" /></ListItemIcon>
                <ListItemText primary={card.label} />
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title="Export data as CSV" arrow>
            <Button data-tour="distribution-export" onClick={exportCsv} variant="outlined" size="small" startIcon={<Download fontSize="small" />} sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}>
              CSV
            </Button>
          </Tooltip>
          <Tooltip title="Export the whole page as PNG" arrow>
            <Button onClick={exportPng} variant="outlined" size="small" startIcon={<ImageIcon fontSize="small" />} sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}>
              PNG
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Summary tiles with W/W deltas */}
      {visibleCards.has('summary') && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <SummaryTile label="Total apps" value={data?.totalApps ?? 0} accent="#6366f1" previous={weekAgo?.totalApps} />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Android"
              value={data?.platformCounts?.android || 0}
              accent="#3ddc84"
              previous={weekAgo?.platformCounts?.android}
              onClick={() => openDrill({ type: 'platform', value: 'android', label: 'Platform: Android' })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="iOS"
              value={data?.platformCounts?.ios || 0}
              accent="#0f172a"
              previous={weekAgo?.platformCounts?.ios}
              onClick={() => openDrill({ type: 'platform', value: 'ios', label: 'Platform: iOS' })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Other"
              value={data?.platformCounts?.other || 0}
              accent="#94a3b8"
              previous={weekAgo?.platformCounts?.other}
              onClick={() => openDrill({ type: 'platform', value: 'other', label: 'Platform: Other' })}
            />
          </Grid>
        </Grid>
      )}

      {/*
        Layout note: the four charts here used to look congested because
          (a) the pie cards' version-chip strip wrapped to two lines, making
              row 1 taller than row 2;
          (b) the bar-chart card titles used MUI `gutterBottom` (~5.6 px),
              so the title sat flush against the chart and the chips above
              looked stacked right on top of it.
        Fix: keep chip strips on a SINGLE horizontally-scrolling line, cap
        at 6 chips, give the bar-chart titles real breathing room, lock
        each chart to a fixed height so rows align cleanly, and widen the
        Grid gap so the four cards never feel mashed together.
      */}
      <Grid container spacing={4} sx={{ mb: 3 }}>
        {visibleCards.has('androidPie') && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Android Version Distribution</Typography>
                <Button size="small" onClick={() => openDrill({ type: 'platform', value: 'android', label: 'Platform: Android' })} sx={{ textTransform: 'none' }}>
                  View apps
                </Button>
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PieChart dataType="android" preCalculatedCounts={versionCounts.android} />
              </Box>
              {Object.keys(versionCounts.android).length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                      // Horizontal-scroll instead of flex-wrap keeps the card
                      // height predictable so it aligns with the bar-chart
                      // card on the row below.
                      overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(15, 23, 42, 0.16)', borderRadius: 3 },
                    }}
                  >
                    {Object.entries(versionCounts.android).slice(0, 6).map(([v, count]) => (
                      <Chip
                        key={v}
                        size="small"
                        label={`v${v} · ${count as number}`}
                        onClick={() => openDrill({ type: 'androidVersion', value: v, label: `Android v${v}` })}
                        sx={{ cursor: 'pointer', fontWeight: 600, bgcolor: 'rgba(61, 220, 132, 0.12)', color: '#1b8a47', flexShrink: 0 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
        {visibleCards.has('iosPie') && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">iOS Version Distribution</Typography>
                <Button size="small" onClick={() => openDrill({ type: 'platform', value: 'ios', label: 'Platform: iOS' })} sx={{ textTransform: 'none' }}>
                  View apps
                </Button>
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PieChart dataType="ios" preCalculatedCounts={versionCounts.ios} pinnedLabel={latestTag || undefined} />
              </Box>
              {Object.keys(versionCounts.ios).length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                      overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(15, 23, 42, 0.16)', borderRadius: 3 },
                    }}
                  >
                    {Object.entries(versionCounts.ios).slice(0, 6).map(([v, count]) => (
                      <Chip
                        key={v}
                        size="small"
                        label={`v${v} · ${count as number}`}
                        onClick={() => openDrill({ type: 'iosVersion', value: v, label: `iOS v${v}` })}
                        sx={{ cursor: 'pointer', fontWeight: 600, bgcolor: 'rgba(15, 23, 42, 0.08)', color: '#0f172a', flexShrink: 0 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {visibleCards.has('platformBar') && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* `mb: 2.5` (was MUI gutterBottom ≈ 5 px) gives the title real
                  separation from the chart canvas — previously the chip row
                  from the row above appeared to be sitting right on top of
                  this title. */}
              <Typography variant="h6" align="center" sx={{ mb: 2.5 }}>Platform Distribution</Typography>
              <Box sx={{ flex: 1, minHeight: 280, position: 'relative' }}>
                <Bar
                  ref={platformChartRef}
                  onClick={(evt) => {
                    const chart = platformChartRef.current;
                    if (!chart) return;
                    const elements = chart.getElementsAtEventForMode(evt.nativeEvent, 'nearest', { intersect: true }, true);
                    if (elements?.length) {
                      const i = elements[0].index;
                      const label = ['android', 'ios', 'other'][i];
                      if (label) openDrill({ type: 'platform', value: label, label: `Platform: ${label}` });
                    }
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { onClick: () => {} } },
                    scales: {
                      x: { ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                      y: { ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                    },
                  }}
                  data={platformData}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Click any bar to see the apps in that category
              </Typography>
            </Paper>
          </Grid>
        )}

        {visibleCards.has('statusBar') && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" align="center" sx={{ mb: 2.5 }}>Deployment Status</Typography>
              <Box sx={{ flex: 1, minHeight: 280, position: 'relative' }}>
                <Bar
                  ref={statusChartRef}
                  onClick={(evt) => {
                    const chart = statusChartRef.current;
                    if (!chart) return;
                    const elements = chart.getElementsAtEventForMode(evt.nativeEvent, 'nearest', { intersect: true }, true);
                    if (elements?.length) {
                      const i = elements[0].index;
                      const label = statusLabels[i];
                      if (label) openDrill({ type: 'status', value: label, label: `Status: ${label}` });
                    }
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y' as const,
                    plugins: { legend: { onClick: () => {} } },
                    scales: {
                      x: { ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                      y: { ticks: { color: '#1a202c', font: { weight: 'bold' } } },
                    },
                  }}
                  data={statusData}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Click any bar to see the apps in that status
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Top 10 most-built apps */}
      {visibleCards.has('topApps') && (
        <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Whatshot sx={{ color: '#f59e0b' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Top 10 most-built apps</Typography>
                <Typography variant="caption" color="text.secondary">By build count over the last 30 days</Typography>
              </Box>
            </Stack>
          </Stack>
          {topAppsLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
          ) : topApps.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">No build activity in the last 30 days.</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {topApps.map((app, i) => {
                const failRate = app.total > 0 ? Math.round((app.failed / app.total) * 100) : 0;
                return (
                  <ListItem
                    key={app.applicationId}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      bgcolor: i % 2 ? 'transparent' : 'rgba(15, 23, 42, 0.02)',
                      transition: 'background-color 150ms ease',
                      '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.06)' },
                    }}
                    secondaryAction={
                      <Tooltip title="View app details" arrow>
                        <IconButton size="small" onClick={() => openAppDetails(app.composerHost, app.platform, app.composerAppId ?? app.composer_app_id)}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Avatar sx={{ width: 26, height: 26, bgcolor: platformColor(app.platform), fontSize: '0.7rem', fontWeight: 700 }}>
                        {platformIcon(app.platform) || '?'}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{app.composerHost}</Typography>
                          <Chip
                            size="small"
                            label={`${app.total} build${app.total !== 1 ? 's' : ''}`}
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#b45309' }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {app.success} success · {app.failed} failed{failRate > 20 ? ` · ⚠ ${failRate}% failure rate` : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      )}

      {/* Drill-down modal */}
      <Dialog open={!!drill} onClose={closeDrill} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {drill && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
                  <RocketLaunchOutlined sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{drill.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {drillResults.length} app{drillResults.length !== 1 ? 's' : ''} matching
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={closeDrill} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
              {drillResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No apps match this criterion right now.</Typography>
              ) : (
                <Grid container spacing={1}>
                  {drillResults.map((app, i) => (
                    <Grid key={`${app.domain}-${i}`} item xs={12} sm={6}>
                      <AppRow app={app} onJump={openAppDetails} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => {
                const text = drillResults.map(a => a.domain).join('\n');
                navigator.clipboard?.writeText(text).catch(() => { /* ignore */ });
              }} startIcon={<ContentCopy fontSize="small" />} sx={{ textTransform: 'none' }}>
                Copy hosts
              </Button>
              <Button onClick={closeDrill} variant="outlined">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// ----- Sub-components -----

const SummaryTile: React.FC<{
  label: string;
  value: number;
  accent: string;
  previous?: number | null;
  onClick?: () => void;
}> = ({ label, value, accent, previous, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: 3,
      border: '1px solid #e2e8f0',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 150ms ease',
      ...(onClick && {
        '&:hover': {
          borderColor: 'rgba(99, 102, 241, 0.4)',
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.06)',
        },
      }),
    }}
  >
    <Typography variant="overline" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.06em' }}>{label}</Typography>
    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</Typography>
    <Box sx={{ mt: 1 }}>
      <DeltaArrow current={value} previous={previous ?? null} />
    </Box>
  </Paper>
);

const AppRow: React.FC<{ app: any; onJump: (host: string, platform?: string, appId?: string | number) => void }> = ({ app, onJump }) => {
  const platform = (app.platform || '').toLowerCase();
  const status = app.status || '';
  const version = app.android_version || app.ios_version || app.version || '—';
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        transition: 'all 150ms ease',
        '&:hover': { borderColor: 'rgba(99, 102, 241, 0.4)', transform: 'translateX(-1px)' },
      }}
    >
      <Avatar variant="rounded" sx={{ width: 30, height: 30, bgcolor: platformColor(platform) }}>
        {platformIcon(platform)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {app.domain || app.app_name || 'Unknown'}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor(status) }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            v{version} · {status || 'N/A'}
          </Typography>
        </Stack>
      </Box>
      <Tooltip title="View app details" arrow>
        <IconButton size="small" onClick={() => onJump(app.domain, app.platform, app.composerAppId ?? app.composer_app_id)} sx={{ color: '#6366f1' }}>
          <OpenInNew sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};
